import type { BoostKind, BoostedCatalogEntry } from '~/services/BoostedCatalogService';

export const BOOST_KINDS: ReadonlyArray<BoostKind> = ['lpp_boost', 'lpc_discount', 'new_lpc'];

export const BOOST_KIND_LABELS: Record<BoostKind, string> = {
  lpp_boost: 'LPP boost',
  lpc_discount: 'LPC discount',
  new_lpc: 'New LPC',
};

export type KindCounts = Record<BoostKind, number>;

export type CatalogFilters = {
  kind: BoostKind | 'all';
  search: string;
};

export type WindowStatus = 'active' | 'ended' | 'none';

export type CatalogRow = BoostedCatalogEntry & {
  id: string;
  windowStatus: WindowStatus;
};

const INVISIBLE_CHAR_CODES = new Set([0x200b, 0x200c, 0x200d, 0xfeff]);

export function cleanName(name: string): string {
  return [...name]
    .filter((char) => !INVISIBLE_CHAR_CODES.has(char.charCodeAt(0)))
    .join('')
    .trim();
}

export function getWindowStatus(endDate: string | null, now: Date): WindowStatus {
  if (!endDate) {
    return 'none';
  }
  return new Date(endDate).getTime() < now.getTime() ? 'ended' : 'active';
}

export function toRows(entries: Array<BoostedCatalogEntry>, now: Date): Array<CatalogRow> {
  return entries.map((entry) => ({
    ...entry,
    id: `${entry.kind}:${entry.offerId}`,
    name: cleanName(entry.name ?? ''),
    place: entry.place ?? '',
    value: entry.value ?? 0,
    impressions7d: entry.impressions7d ?? 0,
    lifetimeImpressions: entry.lifetimeImpressions ?? 0,
    sales7d: entry.sales7d ?? 0,
    windowStatus: getWindowStatus(entry.endDate, now),
  }));
}

export function countByKind(rows: Array<CatalogRow>): KindCounts {
  const counts: KindCounts = { lpp_boost: 0, lpc_discount: 0, new_lpc: 0 };
  rows.forEach((row) => {
    counts[row.kind] += 1;
  });
  return counts;
}

export function filterRows(rows: Array<CatalogRow>, filters: CatalogFilters): Array<CatalogRow> {
  const search = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.kind !== 'all' && row.kind !== filters.kind) {
      return false;
    }
    if (!search) {
      return true;
    }
    return [row.name, row.place, row.offerId].some((field) => field.toLowerCase().includes(search));
  });
}

export function formatBoostValue(kind: BoostKind, value: number): string {
  if (kind === 'lpc_discount') {
    return `${Math.round(value * 100)}%`;
  }
  if (kind === 'new_lpc') {
    return `${value} ${value === 1 ? 'day' : 'days'} left`;
  }
  return value.toFixed(2);
}
